import React, { useRef, useMemo, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Sphere, useTexture } from '@react-three/drei';
import * as THREE from 'three';
import ImagePin from './ImagePin';

// Convert lat/long to 3D Cartesian coordinates on sphere.
// Uses the standard spherical-coordinate formula for an equirectangular Earth
// texture with Prime Meridian centered, East on right, North up.
//   phi   = polar angle (0 = north pole, π = south pole)
//   theta = azimuthal angle (Prime Meridian = π, so we add +180° to lon)
// The negative sign on x flips the X-axis so that East (positive lon) lands
// on the +X side of the sphere — matching how Three.js's default SphereGeometry
// wraps a left-to-right texture.
// A +0.5° lat lift compensates for slight vertical drift in our specific texture.
const latLongToVector3 = (lat, lon, radius = 5) => {
  const phi = (90 - ((lat || 0) + 0.5)) * (Math.PI / 180);
  const theta = ((lon || 0) + 180) * (Math.PI / 180);

  const x = -radius * Math.sin(phi) * Math.cos(theta);
  const y = radius * Math.cos(phi);
  const z = radius * Math.sin(phi) * Math.sin(theta);

  return new THREE.Vector3(x, y, z);
};

// Spinning Globe with real Earth texture and manual control
const Globe = ({ onInteractionStart, onInteractionEnd, isUserControlling, users, onPinClick, currentUserId, BACKEND_URL }) => {
  const meshRef = useRef();
  const continentsRef = useRef();
  const outerGlowRef = useRef();
  const gridRef = useRef();
  const globeGroupRef = useRef(); // Reference for entire globe group
  const neonRim1Ref = useRef(); // For first neon layer
  const neonRim2Ref = useRef(); // For second neon layer
  
  // Load the texture from local public folder
  const earthTexture = useTexture('/earth-texture.png');
  
  // Configure texture wrapping for proper UV mapping
  if (earthTexture) {
    earthTexture.wrapS = THREE.RepeatWrapping;
    earthTexture.wrapT = THREE.ClampToEdgeWrapping;
    // Flip texture vertically to correct upside-down orientation
    earthTexture.flipY = true;
  }

  // Auto-rotation enabled - smooth gentle spin
  useFrame((state, delta) => {
    // Rotate the globe when user isn't manually controlling it
    if (!isUserControlling) {
      if (globeGroupRef.current) {
        globeGroupRef.current.rotation.y += delta * 0.1;
      }
    }
    
    // Keep pulsing glow active for continents
    if (continentsRef.current) {
      const pulse = Math.sin(state.clock.elapsedTime * 0.5) * 0.15 + 1;
      continentsRef.current.material.emissiveIntensity = 0.7 * pulse;
    }
    
    // Animate neon rim layers with different pulse rates
    if (neonRim1Ref.current) {
      const pulse1 = Math.sin(state.clock.elapsedTime * 1.5) * 0.5 + 1.5;
      neonRim1Ref.current.material.emissiveIntensity = 2.0 * pulse1;
    }
    
    if (neonRim2Ref.current) {
      const pulse2 = Math.sin(state.clock.elapsedTime * 2.0 + 1) * 0.3 + 1;
      neonRim2Ref.current.material.opacity = 0.15 * pulse2;
    }
  });

  return (
    <group ref={globeGroupRef} rotation={[0, Math.PI * -0.05, 0]}>
      {/* Rotated OPPOSITE direction - testing mirror theory */}
      {/* Dark Ocean Base Sphere */}
      <Sphere ref={meshRef} args={[5, 64, 64]}>
        <meshStandardMaterial
          color="#0a1628"
          roughness={0.9}
          metalness={0.1}
          emissive="#001a33"
          emissiveIntensity={0.2}
        />
      </Sphere>

      {/* Continents Layer - using texture as alphaMap - INCREASED VISIBILITY */}
      <Sphere ref={continentsRef} args={[5.02, 64, 64]}>
        <meshStandardMaterial
          color="#00d4ff"
          alphaMap={earthTexture}
          transparent={true}
          opacity={0.95}               // Increased from 0.75 - MORE VISIBLE
          emissive="#00d4ff"
          emissiveIntensity={1.2}      // Increased glow
          roughness={0.5}
          metalness={0.4}
        />
      </Sphere>

      {/* Enhanced Wireframe Grid - DIMMED for dark ocean */}
      <Sphere ref={gridRef} args={[5.04, 32, 32]}>
        <meshBasicMaterial
          color="#00ffff"
          wireframe={true}
          transparent
          opacity={0.15}                // Reduced from 0.5 - Much dimmer for black ocean
        />
      </Sphere>

      {/* Inner Rim Glow - Reduced */}
      <Sphere args={[5.08, 32, 32]}>
        <meshStandardMaterial
          color="#00d4ff"
          transparent
          opacity={0.08}               // More transparent
          emissive="#00d4ff"
          emissiveIntensity={0.7}
          side={THREE.FrontSide}
        />
      </Sphere>

      {/* Outer Glow Layer - Lighter Atmosphere */}
      <Sphere ref={outerGlowRef} args={[5.15, 32, 32]}>
        <meshStandardMaterial
          color="#06b6d4"
          transparent
          opacity={0.05}               // More transparent
          roughness={0.3}
          metalness={0.9}
          emissive="#06b6d4"
          emissiveIntensity={0.4}
          side={THREE.BackSide}
        />
      </Sphere>

      {/* NEON OUTER EDGE GLOW - Bright rim effect with pulsing animation */}
      <Sphere ref={neonRim1Ref} args={[5.2, 64, 64]}>
        <meshStandardMaterial
          color="#00ffff"
          transparent
          opacity={0.3}
          emissive="#00ffff"
          emissiveIntensity={2.0}
          side={THREE.BackSide}
          blending={THREE.AdditiveBlending}
        />
      </Sphere>
      
      {/* Additional outer neon ring for more intensity */}
      <Sphere ref={neonRim2Ref} args={[5.25, 64, 64]}>
        <meshBasicMaterial
          color="#00d4ff"
          transparent
          opacity={0.15}
          side={THREE.BackSide}
          blending={THREE.AdditiveBlending}
        />
      </Sphere>

      {/* Orbital Rings */}
      <OrbitalRings isUserControlling={isUserControlling} />
      
      {/* Floating Particles */}
      <FloatingParticles />
      
      {/* User Pins - Inside globe group so they rotate with it */}
      {users && users.length > 0 && (
        <UserPins 
          users={users} 
          onPinClick={onPinClick}
          currentUserId={currentUserId}
          BACKEND_URL={BACKEND_URL}
        />
      )}
    </group>
  );
};


// Floating Particles around the globe
const FloatingParticles = () => {
  const particlesRef = useRef();
  const particleCount = 100;
  
  // Generate random particle positions around the globe
  const particles = useMemo(() => {
    const positions = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i++) {
      const radius = 6 + Math.random() * 4; // Between 6 and 10 units from center
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      
      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = radius * Math.cos(phi);
    }
    return positions;
  }, []);

  // Animate particles floating
  useFrame((state) => {
    if (particlesRef.current) {
      particlesRef.current.rotation.y += 0.0005;
      particlesRef.current.rotation.x += 0.0002;
    }
  });

  return (
    <points ref={particlesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particleCount}
          array={particles}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.06}
        color="#00d4ff"
        transparent
        opacity={0.4}               // More transparent particles
        sizeAttenuation={true}
      />
    </points>
  );
};

// Orbital rings around the globe - Enhanced with manual control
const OrbitalRings = ({ isUserControlling }) => {
  const ring1Ref = useRef();
  const ring2Ref = useRef();
  const ring3Ref = useRef();

  useFrame((state, delta) => {
    // Only animate if user is NOT controlling
    if (!isUserControlling) {
      if (ring1Ref.current) {
        ring1Ref.current.rotation.z += delta * 0.2;
        // Pulsing effect
        const pulse1 = Math.sin(state.clock.elapsedTime * 0.8) * 0.3 + 1;
        ring1Ref.current.material.emissiveIntensity = 1.5 * pulse1;
      }
      if (ring2Ref.current) {
        ring2Ref.current.rotation.z -= delta * 0.15;
        const pulse2 = Math.sin(state.clock.elapsedTime * 0.6 + 1) * 0.3 + 1;
        ring2Ref.current.material.emissiveIntensity = 1.2 * pulse2;
      }
      if (ring3Ref.current) {
        ring3Ref.current.rotation.x += delta * 0.1;
        const pulse3 = Math.sin(state.clock.elapsedTime * 0.5 + 2) * 0.3 + 1;
        ring3Ref.current.material.emissiveIntensity = 1 * pulse3;
      }
    }
  });

  return (
    <group>
      {/* Ring 1 - Horizontal Cyan */}
      <mesh ref={ring1Ref} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[7, 0.025, 16, 100]} />
        <meshStandardMaterial
          color="#00ffff"
          emissive="#00ffff"
          emissiveIntensity={1.5}
          transparent
          opacity={0.5}
          metalness={0.8}
        />
      </mesh>

      {/* Ring 2 - Tilted Purple */}
      <mesh ref={ring2Ref} rotation={[Math.PI / 4, 0, 0]}>
        <torusGeometry args={[8, 0.02, 16, 100]} />
        <meshStandardMaterial
          color="#a855f7"
          emissive="#a855f7"
          emissiveIntensity={1.2}
          transparent
          opacity={0.4}
          metalness={0.8}
        />
      </mesh>

      {/* Ring 3 - Vertical Teal */}
      <mesh ref={ring3Ref} rotation={[0, Math.PI / 2, 0]}>
        <torusGeometry args={[6.5, 0.022, 16, 100]} />
        <meshStandardMaterial
          color="#06b6d4"
          emissive="#06b6d4"
          emissiveIntensity={1}
          transparent
          opacity={0.45}
          metalness={0.8}
        />
      </mesh>
    </group>
  );
};

// User pins on the globe
// User Pins using ImagePin component (PNG sprite)
const UserPins = ({ users, onPinClick, currentUserId, BACKEND_URL }) => {
  return (
    <group>
      {users.map((user, index) => {
        // Radius 5.0 - pin sits directly on globe surface
        const position = latLongToVector3(user.latitude, user.longitude, 5.0);
        const isCurrentUser = user.user_id === currentUserId || user.id === currentUserId;
        
        return (
          <ImagePin
            key={`user-pin-${user.user_id || user.id}-${index}`}
            position={position}
            user={user}
            isCurrentUser={isCurrentUser}
            onClick={(userData, screenPos) => onPinClick(userData, screenPos)}
          />
        );
      })}
    </group>
  );
};

// Main 3D Globe Visualization Component
const GlobeVisualization3D = ({ 
  users = [], 
  onPinClick, 
  showActionMenu = false,
  BACKEND_URL,
  currentUserId,
  spaceVideoUrl1 = null,
  spaceVideoUrl2 = null
}) => {
  const [isUserControlling, setIsUserControlling] = useState(false);
  const inactivityTimerRef = useRef(null);
  const video1RefA = useRef(null);
  const video1RefB = useRef(null);
  const video2RefA = useRef(null);
  const video2RefB = useRef(null);
  const [activeVideo1, setActiveVideo1] = useState('A');
  const [activeVideo2, setActiveVideo2] = useState('A');

  // Seamless video loop handler for layer 1
  const handleVideo1TimeUpdate = (e) => {
    const video = e.target;
    const duration = video.duration;
    const currentTime = video.currentTime;
    
    // 0.5 seconds before end, start the alternate video
    if (duration - currentTime < 0.5) {
      const altVideo = activeVideo1 === 'A' ? video1RefB.current : video1RefA.current;
      if (altVideo && altVideo.paused) {
        altVideo.currentTime = 0;
        altVideo.play();
        setActiveVideo1(activeVideo1 === 'A' ? 'B' : 'A');
      }
    }
  };

  // Seamless video loop handler for layer 2
  const handleVideo2TimeUpdate = (e) => {
    const video = e.target;
    const duration = video.duration;
    const currentTime = video.currentTime;
    
    if (duration - currentTime < 0.5) {
      const altVideo = activeVideo2 === 'A' ? video2RefB.current : video2RefA.current;
      if (altVideo && altVideo.paused) {
        altVideo.currentTime = 0;
        altVideo.play();
        setActiveVideo2(activeVideo2 === 'A' ? 'B' : 'A');
      }
    }
  };

  // Reset to auto-rotate after 5 seconds of inactivity
  const handleUserInteractionStart = () => {
    setIsUserControlling(true);
    
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
  };

  const handleUserInteractionEnd = () => {
    // Set a timer to resume auto-rotation after 10 seconds
    inactivityTimerRef.current = setTimeout(() => {
      setIsUserControlling(false);
    }, 10000); // 10 seconds
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, []);

  // Default camera distance — open less-zoomed on mobile portrait so the
  // globe doesn't fill the entire small viewport. In landscape mobile we
  // pull the camera slightly closer so the globe fills more of the wider
  // landscape canvas.
  const mq = typeof window !== 'undefined' && window.matchMedia;
  const isMobilePortrait = mq && window.matchMedia('(max-width: 768px) and (orientation: portrait)').matches;
  const isMobileLandscape = mq && window.matchMedia('(max-width: 920px) and (orientation: landscape)').matches;
  const initialCameraZ = isMobilePortrait ? 19 : (isMobileLandscape ? 10 : 15);

  return (
    <div style={{ width: '100%', height: '100%', background: '#000000', position: 'relative' }}>
      {/* Video Background Layer 1 - Dual videos for seamless loop */}
      {spaceVideoUrl1 && (
        <>
          <video
            ref={video1RefA}
            autoPlay
            muted
            playsInline
            preload="auto"
            onTimeUpdate={handleVideo1TimeUpdate}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 0,
              opacity: activeVideo1 === 'A' ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out'
            }}
          >
            <source src={spaceVideoUrl1} type="video/mp4" />
          </video>
          <video
            ref={video1RefB}
            muted
            playsInline
            preload="auto"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 0,
              opacity: activeVideo1 === 'B' ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out'
            }}
          >
            <source src={spaceVideoUrl1} type="video/mp4" />
          </video>
        </>
      )}

      {/* Video Background Layer 2 - Dual videos for seamless loop */}
      {spaceVideoUrl2 && (
        <>
          <video
            ref={video2RefA}
            autoPlay
            muted
            playsInline
            preload="auto"
            onTimeUpdate={handleVideo2TimeUpdate}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: activeVideo2 === 'A' ? 0.2 : 0,
              zIndex: 1,
              transition: 'opacity 0.3s ease-in-out'
            }}
          >
            <source src={spaceVideoUrl2} type="video/mp4" />
          </video>
          <video
            ref={video2RefB}
            muted
            playsInline
            preload="auto"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: activeVideo2 === 'B' ? 0.2 : 0,
              zIndex: 1,
              transition: 'opacity 0.3s ease-in-out'
            }}
          >
            <source src={spaceVideoUrl2} type="video/mp4" />
          </video>
        </>
      )}

      {/* 3D Globe Canvas - TOP LAYER */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 2 }}>
        <Canvas
          camera={{ position: [0, 0, initialCameraZ], fov: 50 }}
          gl={{ antialias: true, alpha: true }}
        >
        {/* Enhanced Lighting for depth and pop */}
        <ambientLight intensity={0.4} />
        
        {/* Key light - Front cyan */}
        <pointLight position={[8, 8, 12]} intensity={2.5} color="#00d4ff" />
        
        {/* Fill light - Side purple */}
        <pointLight position={[-12, -8, -8]} intensity={1.5} color="#a855f7" />
        
        {/* Rim lights for edge glow */}
        <pointLight position={[0, 12, 0]} intensity={1} color="#06b6d4" />
        <pointLight position={[0, -12, 0]} intensity={0.8} color="#8b5cf6" />
        
        {/* Accent lights */}
        <spotLight position={[15, 5, 5]} intensity={1.5} color="#00ffff" angle={0.4} penumbra={0.5} />
        <spotLight position={[-15, -5, 5]} intensity={1} color="#a855f7" angle={0.4} penumbra={0.5} />
        
        {/* Globe with integrated pins */}
        <Globe 
          isUserControlling={isUserControlling}
          users={users}
          onPinClick={onPinClick}
          currentUserId={currentUserId}
          BACKEND_URL={BACKEND_URL}
        />

        {/* Controls with manual interaction detection */}
        <OrbitControls
          enableZoom={true}
          enablePan={false}
          minDistance={7}  // Closer zoom (was 10)
          maxDistance={25}
          autoRotate={false}
          rotateSpeed={0.5}
          onStart={handleUserInteractionStart}
          onEnd={handleUserInteractionEnd}
        />
      </Canvas>
      </div>
    </div>
  );
};

export default GlobeVisualization3D;
